import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AreaChartOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import type { NeoConversationRichContentPart } from "../../../hexagon/core/ports/driven/neoConversationRepository";
import NeoInputFormCard from "../../../pages/Neo/NeoInputFormCard";
import NeoFlagCard, {
  type NeoFlagCardContentPart,
} from "../../neo/FlagIntroCard";
import { richContentKey } from "./richContent";
import styles from "./styles.module.scss";

interface ChartPoint {
  t: string;
  y: number;
  raw?: string;
}

interface ChartSeries {
  id: string;
  label?: string;
  unit?: string;
  points?: ChartPoint[];
  truncated?: boolean;
}

const CHART_COLORS = [
  "#2878c8",
  "#8f55d8",
  "#2f9d69",
  "#d9822b",
  "#c94f7c",
  "#5e6ad2",
];
const CHART_WIDTH = 720;
const CHART_HEIGHT = 280;
const CHART_MARGIN = { top: 20, right: 24, bottom: 42, left: 56 };

const formatNumber = (value: number): string => {
  if (Number.isInteger(value) || Math.abs(value) >= 100) {
    return value.toFixed(0);
  }
  return value.toFixed(Math.abs(value) >= 10 ? 1 : 2).replace(/\.?0+$/, "");
};

const formatTimestamp = (timestamp: string | number): string => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ChartCard = ({ part }: { part: NeoConversationRichContentPart }) => {
  const title =
    typeof part.title === "string" && part.title.trim() ? part.title : "Chart";
  const series = Array.isArray(part.series)
    ? (part.series as unknown as ChartSeries[])
    : [];
  const plot = useMemo(() => {
    const samples = series.flatMap((item) =>
      (Array.isArray(item.points) ? item.points : []).flatMap((point) => {
        const time = Date.parse(point.t);
        return Number.isFinite(time) && Number.isFinite(point.y)
          ? [{ item, point, time }]
          : [];
      })
    );
    if (samples.length === 0) return null;
    const minTime = Math.min(...samples.map(({ time }) => time));
    const maxTime = Math.max(...samples.map(({ time }) => time));
    const values = samples.map(({ point }) => point.y);
    let minValue = Math.min(...values);
    let maxValue = Math.max(...values);
    if (minValue === maxValue) {
      const padding = Math.max(1, Math.abs(minValue) * 0.08);
      minValue -= padding;
      maxValue += padding;
    }
    const width = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
    const height = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;
    const x = (time: number) =>
      CHART_MARGIN.left +
      ((time - minTime) / Math.max(1, maxTime - minTime)) * width;
    const y = (value: number) =>
      CHART_MARGIN.top +
      ((maxValue - value) / Math.max(1, maxValue - minValue)) * height;
    return {
      minTime,
      maxTime,
      minValue,
      maxValue,
      lines: series.map((item, index) => ({
        ...item,
        color: CHART_COLORS[index % CHART_COLORS.length],
        points: (item.points ?? []).flatMap((point) => {
          const time = Date.parse(point.t);
          return Number.isFinite(time) && Number.isFinite(point.y)
            ? [{ ...point, x: x(time), y: y(point.y) }]
            : [];
        }),
      })),
    };
  }, [series]);

  const yTicks = plot
    ? Array.from({ length: 5 }, (_, index) => {
        const ratio = index / 4;
        return {
          y:
            CHART_MARGIN.top +
            ratio * (CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom),
          value: plot.maxValue - (plot.maxValue - plot.minValue) * ratio,
        };
      })
    : [];
  const xTicks = plot
    ? Array.from({ length: 4 }, (_, index) => {
        const ratio = index / 3;
        return {
          x:
            CHART_MARGIN.left +
            ratio * (CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right),
          time: plot.minTime + (plot.maxTime - plot.minTime) * ratio,
        };
      })
    : [];

  return (
    <section className={styles.richCard}>
      <div className={styles.richCardHeading}>
        <AreaChartOutlined />
        <div>
          <h3>{title}</h3>
          {typeof part.description === "string" && <p>{part.description}</p>}
        </div>
      </div>
      {plot ? (
        <>
          <div className={styles.chartViewport}>
            <svg
              className={styles.chart}
              viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
              role="img"
              aria-label={title}
            >
              {yTicks.map((tick) => (
                <g key={tick.y}>
                  <line
                    x1={CHART_MARGIN.left}
                    x2={CHART_WIDTH - CHART_MARGIN.right}
                    y1={tick.y}
                    y2={tick.y}
                    className={styles.chartGrid}
                  />
                  <text
                    x={CHART_MARGIN.left - 8}
                    y={tick.y + 4}
                    textAnchor="end"
                    className={styles.chartAxis}
                  >
                    {formatNumber(tick.value)}
                  </text>
                </g>
              ))}
              {xTicks.map((tick) => (
                <text
                  key={tick.x}
                  x={tick.x}
                  y={CHART_HEIGHT - 10}
                  textAnchor="middle"
                  className={styles.chartAxis}
                >
                  {formatTimestamp(tick.time)}
                </text>
              ))}
              {plot.lines.map((line) =>
                line.points.length ? (
                  <g key={line.id}>
                    <polyline
                      points={line.points
                        .map((point) => `${point.x},${point.y}`)
                        .join(" ")}
                      fill="none"
                      stroke={line.color}
                      strokeWidth="2.5"
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                    {line.points.length <= 80 &&
                      line.points.map((point) => (
                        <circle
                          key={`${line.id}-${point.t}-${point.y}`}
                          cx={point.x}
                          cy={point.y}
                          r="3"
                          fill={line.color}
                        >
                          <title>
                            {formatTimestamp(point.t)} · {line.label || line.id}
                            : {formatNumber(point.y)}
                            {line.unit ? ` ${line.unit}` : ""}
                          </title>
                        </circle>
                      ))}
                  </g>
                ) : null
              )}
            </svg>
          </div>
          <div className={styles.chartLegend}>
            {plot.lines.map((line) => (
              <span key={line.id}>
                <i style={{ background: line.color }} />
                {line.label || line.id}
                {line.unit ? ` (${line.unit})` : ""}
              </span>
            ))}
          </div>
          {plot.lines.some((line) => line.truncated) && (
            <p className={styles.richNotice}>
              Large series downsampled for display.
            </p>
          )}
        </>
      ) : (
        <p className={styles.richEmpty}>No chart points returned.</p>
      )}
    </section>
  );
};

interface MapMarker {
  lng: number;
  lat: number;
  role?: string;
  order?: number;
  label?: string;
  address?: string;
}

const MapCard = ({ part }: { part: NeoConversationRichContentPart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [interactiveFailed, setInteractiveFailed] = useState(false);
  const title =
    typeof part.title === "string" && part.title.trim() ? part.title : "Map";
  const imageUrl =
    typeof part.image_url === "string" ? part.image_url.trim() : "";
  const markers = Array.isArray(part.markers)
    ? (part.markers as unknown as MapMarker[]).filter(
        (marker) => Number.isFinite(marker?.lng) && Number.isFinite(marker?.lat)
      )
    : [];
  const canRenderInteractive =
    part.interactive === true &&
    typeof part.access_token === "string" &&
    part.access_token.length > 0 &&
    markers.length > 0 &&
    !interactiveFailed;
  const signature = JSON.stringify({
    token: part.access_token,
    style: part.style,
    markers,
    route: part.route,
    bbox: part.bbox,
  });

  useEffect(() => {
    if (!canRenderInteractive || !containerRef.current) return;
    let cancelled = false;
    let map: import("mapbox-gl").Map | null = null;
    void (async () => {
      try {
        const [{ default: mapboxgl }] = await Promise.all([
          import("mapbox-gl"),
          import("mapbox-gl/dist/mapbox-gl.css"),
        ]);
        if (cancelled || !containerRef.current) return;
        mapboxgl.accessToken = String(part.access_token);
        const bounds = new mapboxgl.LngLatBounds();
        markers.forEach((marker) => bounds.extend([marker.lng, marker.lat]));
        const route =
          part.route &&
          typeof part.route === "object" &&
          (part.route as { type?: unknown }).type === "LineString"
            ? (part.route as GeoJSON.LineString)
            : null;
        route?.coordinates.forEach((coordinate) => {
          if (
            Number.isFinite(coordinate[0]) &&
            Number.isFinite(coordinate[1])
          ) {
            bounds.extend([coordinate[0], coordinate[1]]);
          }
        });
        map = new mapboxgl.Map({
          container: containerRef.current,
          style:
            typeof part.style === "string"
              ? part.style
              : "mapbox://styles/mapbox/streets-v12",
          bounds,
          fitBoundsOptions: { padding: 44, maxZoom: 15 },
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        map.on("load", () => {
          if (!map || cancelled) return;
          if (route && route.coordinates.length >= 2) {
            map.addSource("copilot-route", {
              type: "geojson",
              data: { type: "Feature", properties: {}, geometry: route },
            });
            map.addLayer({
              id: "copilot-route",
              type: "line",
              source: "copilot-route",
              paint: { "line-color": "#2563eb", "line-width": 4 },
              layout: { "line-cap": "round", "line-join": "round" },
            });
          }
          markers.forEach((marker, index) => {
            const node = document.createElement("div");
            node.className = styles.mapMarker;
            node.textContent =
              marker.role === "start" ? "S" : String(marker.order ?? index + 1);
            new mapboxgl.Marker({ element: node })
              .setLngLat([marker.lng, marker.lat])
              .setPopup(
                new mapboxgl.Popup({ offset: 16 }).setText(
                  marker.label || marker.address || "Map location"
                )
              )
              .addTo(map!);
          });
        });
        map.on("error", () => {
          if (!cancelled) setInteractiveFailed(true);
        });
      } catch {
        if (!cancelled) setInteractiveFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [canRenderInteractive, signature]);

  const summary =
    part.summary && typeof part.summary === "object"
      ? (part.summary as Record<string, unknown>)
      : {};
  const summaryBits = [
    typeof summary.stop_count === "number"
      ? `${summary.stop_count} stop${summary.stop_count === 1 ? "" : "s"}`
      : null,
    summary.roundtrip === true ? "round trip" : null,
    typeof summary.duration_minutes === "number"
      ? `${Math.round(summary.duration_minutes)} min`
      : null,
    typeof summary.distance_miles === "number"
      ? `${summary.distance_miles.toFixed(1)} mi`
      : null,
  ].filter(Boolean);

  if (!canRenderInteractive && !imageUrl) return null;
  return (
    <section className={styles.richCard}>
      <div className={styles.richCardHeading}>
        <EnvironmentOutlined />
        <h3>{title}</h3>
      </div>
      {canRenderInteractive ? (
        <div
          ref={containerRef}
          className={styles.interactiveMap}
          role="application"
          aria-label={title}
        />
      ) : (
        <img
          className={styles.mapImage}
          src={imageUrl}
          alt={typeof part.image_alt === "string" ? part.image_alt : title}
          loading="lazy"
        />
      )}
      {summaryBits.length > 0 && (
        <p className={styles.mapSummary}>{summaryBits.join(" · ")}</p>
      )}
    </section>
  );
};

const DashboardCard = ({ part }: { part: NeoConversationRichContentPart }) => {
  const navigate = useNavigate();
  const dashboardId =
    typeof part.dashboard_id === "number" ? part.dashboard_id : null;
  const projectId =
    typeof part.project_id === "number" ? part.project_id : null;
  if (dashboardId == null || projectId == null) return null;
  const title =
    typeof part.name === "string" && part.name.trim() ? part.name : "Dashboard";
  return (
    <button
      type="button"
      className={styles.dashboardCard}
      onClick={() =>
        navigate(`/neo?agent=${projectId}&dashboard=${dashboardId}`)
      }
    >
      <AreaChartOutlined />
      <span>
        <small>
          Dashboard {part.action === "created" ? "created" : "updated"}
        </small>
        <strong>{title}</strong>
      </span>
      <b>Open →</b>
    </button>
  );
};

const RichContentBlocks = ({
  parts,
  onSubmitForm,
}: {
  parts: NeoConversationRichContentPart[];
  onSubmitForm?: (text: string) => void | Promise<void>;
}) => {
  const submit = useCallback(
    async (text: string) => {
      await onSubmitForm?.(text);
    },
    [onSubmitForm]
  );

  return (
    <div className={styles.richContent}>
      {parts.map((part) => {
        const key = richContentKey(part);
        switch (part.type) {
          case "neo_chart":
            return <ChartCard key={key} part={part} />;
          case "neo_map":
            return <MapCard key={key} part={part} />;
          case "neo_form":
            return (
              <NeoInputFormCard
                key={key}
                form={part}
                disabled={!onSubmitForm}
                onSubmit={submit}
                onCancel={(message) => void submit(message)}
              />
            );
          case "neo_dashboard":
            return <DashboardCard key={key} part={part} />;
          case "neo_flag_card":
            return (
              <NeoFlagCard
                key={key}
                card={part as unknown as NeoFlagCardContentPart}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

export default RichContentBlocks;
